import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Button.css';

/**
 * Premium Button Component with enhanced visual effects
 * 
 * Features:
 * - Ripple animation effect on click
 * - Loading state with spinner
 * - Success/Error feedback states
 * - Accessible animations with reduced motion support
 * 
 * @param {Object} props - Component props
 * @returns {React.Component} Button component
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  type = 'button',
  disabled = false,
  loading = false,
  success = false,
  error = false,
  fullWidth = false,
  icon = null,
  iconPosition = 'left',
  onClick,
  className = '',
  ...rest
}) => {
  const [ripples, setRipples] = useState([]);
  const [feedback, setFeedback] = useState(null);
  
  // Handle ripple effect on click
  const handleClick = (e) => {
    if (disabled || loading) return;
    
    // Get button dimensions and click position
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const left = e.clientX - rect.left;
    const top = e.clientY - rect.top;
    
    // Create a new ripple
    const ripple = { left, top, id: Date.now() };
    setRipples([...ripples, ripple]);
    
    // Remove ripple after animation completes
    setTimeout(() => {
      setRipples(prevRipples => prevRipples.filter(r => r.id !== ripple.id));
    }, 1000);
    
    // Call the provided onClick handler
    if (onClick) onClick(e);
  };
  
  // Handle success/error feedback states
  useEffect(() => {
    if (success) {
      setFeedback('success');
      const timer = setTimeout(() => setFeedback(null), 2000);
      return () => clearTimeout(timer);
    } else if (error) {
      setFeedback('error');
      const timer = setTimeout(() => setFeedback(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);
  
  // Determine button classes based on props
  const buttonClasses = [
    'premium-button',
    `premium-button--${variant}`,
    `premium-button--${size}`,
    loading ? 'premium-button--loading' : '',
    disabled ? 'premium-button--disabled' : '',
    fullWidth ? 'premium-button--full-width' : '',
    feedback ? `premium-button--${feedback}` : '',
    className
  ].filter(Boolean).join(' ');
  
  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      {...rest}
    >
      {/* Loading spinner */}
      {loading && (
        <span className="premium-button__spinner" aria-hidden="true">
          <span className="premium-button__spinner-dot"></span>
          <span className="premium-button__spinner-dot"></span>
          <span className="premium-button__spinner-dot"></span>
        </span>
      )}
      
      {/* Button content */}
      <span className="premium-button__content">
        {icon && iconPosition === 'left' && (
          <span className="premium-button__icon premium-button__icon--left">
            {icon}
          </span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="premium-button__icon premium-button__icon--right">
            {icon}
          </span>
        )}
      </span>
      
      {/* Ripple effects */}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="premium-button__ripple"
          style={{
            left: ripple.left,
            top: ripple.top
          }}
        />
      ))}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'danger', 'success', 'outline', 'text']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  onClick: PropTypes.func,
  className: PropTypes.string
};

export default Button;
